/**
 * Shared ordering by the uploaded Schedule of Events.
 *
 * studyDesign/config.scheduleFormOrder holds matched CRF form names in the
 * exact row order of the uploaded SOE — the same order the on-screen Form
 * Assignment Matrix renders. Every export that lists forms (visit/form matrix
 * CSV + DOCX, ODM-XML, printable packet) sorts by this so the outputs mirror
 * what the user sees.
 */

/** Case/punctuation-insensitive, Unicode-aware (letters and digits of any script are kept) form-name key for matching across surfaces. */
export function normalizeFormName(s: string): string {
  return (s || '').normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

/**
 * Stable-sort `items` to match `scheduleFormOrder`. Items whose name isn't in
 * the order keep their original relative order, placed after all ordered ones.
 * Returns the input unchanged when no order is provided.
 */
export function orderByScheduleFormOrder<T>(
  items: T[],
  getName: (item: T) => string,
  scheduleFormOrder?: string[],
): T[] {
  if (!scheduleFormOrder || scheduleFormOrder.length === 0) return items;
  const rank = new Map<string, number>();
  scheduleFormOrder.forEach((n, i) => {
    const k = normalizeFormName(n);
    if (k && !rank.has(k)) rank.set(k, i);
  });
  const rankOf = (item: T) => {
    const r = rank.get(normalizeFormName(getName(item)));
    return r === undefined ? Number.MAX_SAFE_INTEGER : r;
  };
  return items
    .map((item, i) => ({ item, i }))
    .sort((a, b) => {
      const ra = rankOf(a.item);
      const rb = rankOf(b.item);
      return ra !== rb ? ra - rb : a.i - b.i;
    })
    .map((x) => x.item);
}
